/**
 * fab-frame-layouts.js (v3)
 *
 * Behaviour for the editorial frame picker layout.
 *
 * Each visible card wraps a real <label for="radio-id"> so clicking
 * triggers the radio natively. This JS listens for the radio's
 *   `change` event to update the selected-class on cards, then lets
 *   `product-form.js` and `frame-price-updater.js` do the rest.
 *
 * Show-more button:
 *   Reveals a separate tail grid so default-visible swatches do not reflow.
 */

(function () {
  'use strict';

  function init(root) {
    const fieldsets = root.querySelectorAll('.fab-frame-layout');
    fieldsets.forEach(initFieldset);
    if (fieldsets.length) {
      bindXxlFrameBySizeListeners();
      refreshAllXxlFrames();
    }
  }


  function initFieldset(fieldset) {
    if (fieldset.dataset.fabInit === '1') return;
    fieldset.dataset.fabInit = '1';

    const layout = fieldset.dataset.layout || 'editorial';

    // Sync selected-class whenever a frame radio changes (labels handle the click natively)
    fieldset.addEventListener('change', function (e) {
      const target = e.target;
      if (!target || target.tagName !== 'INPUT') return;
      if (!target.classList.contains('fab-frame-radio')) return;
      syncSelectedClass(fieldset, target);
    });

    // Editorial layout behaviour
    if (layout === 'editorial') {
      initShowMoreToggle(fieldset);
    }

    initXxlFrameBySize(fieldset);
  }

  var XXL_SIZE_KEY = '36x48';
  var BOX_SIZE_KEY = '30x60';
  var XXL_ALLOWED_FRAME_KEYS = {
    unframed: true,
    black: true,
    white: true,
    oak: true,
  };

  /**
   * Art prints only: at 90×120 / 36×48 (portrait or landscape), only
   * unframed plus black, white, and oak frames are offered.
   * Canvas products are unchanged.
   */
  function getVariantSelectsRoot(fieldset) {
    return fieldset.closest('variant-selects') || fieldset.closest('product-info');
  }

  function normalizeSizeKey(raw) {
    if (!raw) return '';
    if (window.FabSizeKey && typeof window.FabSizeKey.isXxlSizeKey === 'function') {
      if (window.FabSizeKey.isXxlSizeKey(raw)) return XXL_SIZE_KEY;
    }
    if (window.FabSizeKey && typeof window.FabSizeKey.normalizePopularKey === 'function') {
      var popularKey = window.FabSizeKey.normalizePopularKey(raw);
      if (popularKey) return popularKey;
    }
    return '';
  }

  function is36x48Size(raw) {
    if (window.FabSizeKey && typeof window.FabSizeKey.isXxlSizeKey === 'function') {
      return window.FabSizeKey.isXxlSizeKey(raw);
    }
    return normalizeSizeKey(raw) === XXL_SIZE_KEY;
  }

  function is30x60Size(raw) {
    if (!raw) return false;
    if (normalizeSizeKey(raw) === BOX_SIZE_KEY) return true;
    var s = String(raw)
      .toLowerCase()
      .replace(/\u00d7/g, 'x')
      .replace(/×/g, 'x')
      .replace(/\s+/g, '');
    return (
      /(?:30x60|60x30)in\b/.test(s) ||
      /(?:75x150|150x75)cm\b/.test(s)
    );
  }

  function variantIncludes36x48(variant) {
    if (!variant || !Array.isArray(variant.options)) return false;
    return variant.options.some(function (opt) {
      return is36x48Size(opt);
    });
  }

  function variantIncludes30x60(variant) {
    if (!variant || !Array.isArray(variant.options)) return false;
    return variant.options.some(function (opt) {
      return is30x60Size(opt);
    });
  }

  function isXxlAllowedFrameKey(key) {
    return !!XXL_ALLOWED_FRAME_KEYS[String(key || '').toLowerCase()];
  }

  function getSelectedSizeValue(fieldset) {
    var root = getVariantSelectsRoot(fieldset) || document;

    var selectedCell = root.querySelector(
      '[data-fab-size-cell].fab-size__cell--selected, [data-fab-size-cell].fab-size__asize-cell--selected'
    );
    if (selectedCell && selectedCell.dataset.fabSizeValue) {
      return selectedCell.dataset.fabSizeValue;
    }

    var fabSelect = root.querySelector('.fab-size__select-shim');
    if (fabSelect && fabSelect.value) return fabSelect.value;

    var sizeSelect = root.querySelector(
      'select[name*="Size"], select[name*="size"], select[name*="Taille"], select[name*="taille"], select[name*="Größe"], select[name*="grosse"], select[name*="Tamaño"], select[name*="Dimensione"], select[name*="Maat"], select[name*="Storlek"]'
    );
    if (sizeSelect && sizeSelect.value) return sizeSelect.value;

    try {
      var selected = root.querySelector('[data-selected-variant]');
      if (selected) {
        var variant = JSON.parse(selected.textContent || '{}');
        if (variant && Array.isArray(variant.options)) {
          for (var i = 0; i < variant.options.length; i++) {
            if (is36x48Size(variant.options[i]) || is30x60Size(variant.options[i])) {
              return variant.options[i];
            }
          }
          if (variant.options[0]) return variant.options[0];
        }
      }
    } catch (_) {}

    return '';
  }

  function setRestrictedFrames(fieldset, restrict) {
    fieldset.querySelectorAll('[data-frame-card][data-frame-key]').forEach(function (card) {
      var key = (card.getAttribute('data-frame-key') || '').toLowerCase();
      var hide = restrict && !isXxlAllowedFrameKey(key);
      card.classList.toggle('fab-frame-card--size-hidden', hide);
      card.toggleAttribute('hidden', hide);
      if (hide) card.setAttribute('aria-hidden', 'true');
      else card.removeAttribute('aria-hidden');
      var radio = card.querySelector('input.fab-frame-radio');
      if (radio) radio.disabled = hide;
    });
    updateMoreToggleForXxl(fieldset);
  }

  function setBoxFrameLabels(fieldset, useBoxLabel) {
    fieldset.querySelectorAll('[data-frame-card]').forEach(function (card) {
      var key = (card.getAttribute('data-frame-key') || '').toLowerCase();
      if (key === 'unframed') return;
      var benefit = card.querySelector(
        '.fab-frame-pill-benefit[data-frame-standard-label][data-frame-box-label]'
      );
      if (!benefit) return;
      benefit.textContent = useBoxLabel
        ? benefit.dataset.frameBoxLabel
        : benefit.dataset.frameStandardLabel;
    });
  }

  function updateMoreToggleForXxl(fieldset) {
    var tail = fieldset.querySelector('[data-frame-more-tail]');
    var toggle = fieldset.querySelector('[data-frame-more-toggle]');
    if (!tail || !toggle) return;

    var visibleTail = tail.querySelectorAll(
      '[data-frame-card]:not(.fab-frame-card--size-hidden)'
    );
    var hideToggle = visibleTail.length === 0;
    toggle.toggleAttribute('hidden', hideToggle);
    if (hideToggle) {
      toggle.setAttribute('aria-hidden', 'true');
      toggle.setAttribute('aria-expanded', 'false');
      var layout = fieldset.dataset.layout || '';
      var tailCollapsedClassMap = {
        editorial: 'fab-frame-editorial__tail--collapsed',
      };
      var expandedClassMap = {
        editorial: 'fab-frame-editorial__more--expanded',
      };
      var collapsedClass = tailCollapsedClassMap[layout];
      var expandedClass = expandedClassMap[layout];
      if (collapsedClass) tail.classList.add(collapsedClass);
      if (expandedClass) toggle.classList.remove(expandedClass);
      var collapsedText = toggle.querySelector('[data-frame-more-collapsed]');
      var expandedText = toggle.querySelector('[data-frame-more-expanded]');
      var pips = toggle.querySelector('[data-frame-more-pips]');
      if (collapsedText) collapsedText.style.display = '';
      if (expandedText) expandedText.style.display = 'none';
      if (pips) pips.style.display = '';
    } else {
      toggle.removeAttribute('aria-hidden');
    }
  }

  function deselectRestrictedIfNeeded(fieldset) {
    var checked = fieldset.querySelector('input.fab-frame-radio:checked');
    var card = checked && checked.closest('[data-frame-card]');
    if (!card || !card.classList.contains('fab-frame-card--size-hidden')) return;

    var fallback =
      fieldset.querySelector(
        '[data-frame-key="black"]:not(.fab-frame-card--size-hidden) input.fab-frame-radio'
      ) ||
      fieldset.querySelector(
        '[data-frame-key="white"]:not(.fab-frame-card--size-hidden) input.fab-frame-radio'
      ) ||
      fieldset.querySelector(
        '[data-frame-key="oak"]:not(.fab-frame-card--size-hidden) input.fab-frame-radio'
      ) ||
      fieldset.querySelector(
        '[data-frame-card]:not(.fab-frame-card--size-hidden) input.fab-frame-radio'
      );

    if (!fallback || fallback === checked) return;

    fallback.checked = true;
    fallback.dispatchEvent(new Event('change', { bubbles: true }));
    syncSelectedClass(fieldset, fallback);
  }

  function applyXxlFrameBySize(fieldset, variant) {
    if (fieldset.classList.contains('fab-frame-layout--canvas-product')) return;

    var restrictXxl =
      (variant && variantIncludes36x48(variant)) ||
      is36x48Size(getSelectedSizeValue(fieldset));
    var isWideOrTall =
      fieldset.classList.contains('fab-frame-layout--art-wide') ||
      fieldset.classList.contains('fab-frame-layout--art-tall');
    var restrictBox =
      isWideOrTall &&
      ((variant && variantIncludes30x60(variant)) ||
        is30x60Size(getSelectedSizeValue(fieldset)));
    var restrict = restrictXxl || restrictBox;

    setRestrictedFrames(fieldset, restrict);
    setBoxFrameLabels(fieldset, restrictBox);
    if (restrict) deselectRestrictedIfNeeded(fieldset);
  }

  function refreshAllXxlFrames(variant) {
    document.querySelectorAll('.fab-frame-layout').forEach(function (fieldset) {
      applyXxlFrameBySize(fieldset, variant);
    });
  }

  function bindXxlFrameBySizeListeners() {
    if (window.__fabXxlFrameBySizeBound) return;
    window.__fabXxlFrameBySizeBound = true;

    function scheduleRefresh(variant) {
      requestAnimationFrame(function () {
        refreshAllXxlFrames(variant);
      });
    }

    document.addEventListener('variant:change', function (e) {
      scheduleRefresh(e.detail);
    });

    document.addEventListener(
      'change',
      function (e) {
        var target = e.target;
        if (!target) return;
        if (
          (target.classList && target.classList.contains('fab-size__select-shim')) ||
          (target.tagName === 'SELECT' &&
            /size|taille|größe|grosse|tamaño|dimensione|maat|storlek/i.test(target.name || ''))
        ) {
          scheduleRefresh();
        }
      },
      true
    );

    document.addEventListener(
      'click',
      function (e) {
        if (e.target.closest('[data-fab-size-cell]')) scheduleRefresh();
      },
      true
    );
  }

  function initXxlFrameBySize(fieldset) {
    applyXxlFrameBySize(fieldset);
    bindXxlFrameBySizeListeners();
  }

  /**
   * Move the layout's selected class to the card wrapping the newly-checked radio.
   */
  function syncSelectedClass(fieldset, radio) {
    const layout = fieldset.dataset.layout;
    const selectedClassMap = {
      editorial: 'fab-frame-editorial__card--selected',
    };
    const selectedClass = selectedClassMap[layout];
    if (!selectedClass) return;

    fieldset
      .querySelectorAll('[data-frame-card].' + selectedClass)
      .forEach(function (el) { el.classList.remove(selectedClass); });

    const newSelectedCard = radio.closest('[data-frame-card]');
    if (newSelectedCard) newSelectedCard.classList.add(selectedClass);
  }

  /**
   * Editorial: reveal a separate tail grid so the
   * default-visible swatches keep their positions (no CSS grid reflow).
   */
  function initShowMoreToggle(fieldset) {
    const toggle = fieldset.querySelector('[data-frame-more-toggle]');
    const tail = fieldset.querySelector('[data-frame-more-tail]');
    if (!toggle || !tail) return;

    const layout = fieldset.dataset.layout;
    const tailCollapsedClassMap = {
      editorial: 'fab-frame-editorial__tail--collapsed',
    };
    const expandedClassMap = {
      editorial: 'fab-frame-editorial__more--expanded',
    };
    const tailCollapsedClass = tailCollapsedClassMap[layout];
    const expandedClass = expandedClassMap[layout];

    const collapsedText = toggle.querySelector('[data-frame-more-collapsed]');
    const expandedText = toggle.querySelector('[data-frame-more-expanded]');
    const pips = toggle.querySelector('[data-frame-more-pips]');

    toggle.addEventListener('click', function () {
      const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
      const next = !isExpanded;
      toggle.setAttribute('aria-expanded', next ? 'true' : 'false');

      if (tailCollapsedClass) {
        tail.classList.toggle(tailCollapsedClass, !next);
      }

      if (collapsedText) collapsedText.style.display = next ? 'none' : '';
      if (expandedText) expandedText.style.display = next ? '' : 'none';
      if (pips) pips.style.display = next ? 'none' : '';

      if (next) toggle.classList.add(expandedClass);
      else toggle.classList.remove(expandedClass);
    });
  }

  if (document.readyState !== 'loading') {
    init(document);
  } else {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
  document.addEventListener('product-info:loaded', function (e) {
    init(e.target || document);
  });
})();
